import { Link } from 'react-router-dom'
import SEOHead from '../components/seo/SEOHead'
import { FAIR_USE_POLICY_PATH, LEGAL_CONTACT_EMAIL } from '../config/legal'

export default function SubscriptionTerms() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-navy-900 py-16">
      <SEOHead
        title="Subscription Terms | learn.rondanini.com"
        description="Subscription billing, cancellation, refund, and digital content waiver terms for learn.rondanini.com."
        canonicalUrl="https://learn.rondanini.com/subscription-terms"
        ogType="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Subscription Terms',
          url: 'https://learn.rondanini.com/subscription-terms',
          description:
            'Subscription billing, cancellation, refund, and digital content waiver terms.',
          publisher: {
            '@type': 'Organization',
            name: 'Rondanini Publishing Ltd',
            url: 'https://learn.rondanini.com',
          },
        }}
      />
      <article className="max-w-4xl mx-auto px-4 container-padding prose prose-lg dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-li:text-gray-700 dark:prose-li:text-gray-300">
        <h1>Subscription Terms</h1>
        <p>
          <em>Last updated: 26 March 2026 | Rondanini Publishing Ltd</em>
        </p>

        <h2>1. Who we are</h2>
        <p>
          learn.rondanini.com is operated by Rondanini Publishing Ltd, registered in England &amp; Wales, Suite 6438,
          Unit 3A, 34–35 Hatton Garden, London EC1N 8HX. Contact:{' '}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>
        </p>

        <h2>2. What your subscription includes</h2>
        <p>
          Your subscription grants you personal, non-transferable access to the digital content, tools, and learning
          materials available under your chosen plan during the active subscription period. Access is for your individual
          use only and may not be shared, resold, or redistributed. Use of the Platform is also subject to our{' '}
          <Link to={FAIR_USE_POLICY_PATH}>Fair Use &amp; Access Policy</Link>.
        </p>

        <h2>3. Billing and renewal</h2>
        <p>
          Subscriptions are billed in advance on a recurring basis (monthly or annual, depending on the plan selected).
          Your subscription renews automatically at the end of each billing period unless you cancel before the renewal
          date. Payments are processed by Stripe Payments Europe Ltd on behalf of Rondanini Publishing Ltd.
        </p>

        <h2>4. Cancellation</h2>
        <p>
          You may cancel your subscription at any time via your account settings or by contacting us at{' '}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>. Cancellation takes effect at the end of the
          current billing period. You retain access until that date. No refund is issued for the remainder of any paid
          billing period, except where required by applicable law.
        </p>

        <h2>5. Your right to cancel — digital content waiver</h2>
        <p>
          Under the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013, you ordinarily
          have a 14-day right to cancel a distance contract. However, in accordance with Regulation 37, where you have
          expressly requested that we begin supplying digital content immediately and acknowledged that this waives your
          cancellation right, that right is lost once access to your content is granted.
        </p>
        <p>
          By completing your purchase and confirming your consent at checkout, you acknowledge that: (a) you have
          requested immediate access to the digital content; (b) you accept that your 14-day right to cancel is waived
          from the moment access is granted; and (c) you have been provided this information in advance of purchase.
        </p>
        <p>
          This provision applies to individual consumers (B2C). It does not affect any statutory rights that cannot be
          excluded by law.
        </p>

        <h2>6. Refunds</h2>
        <p>
          Except where required by law or at our sole discretion in exceptional circumstances, subscription fees are
          non-refundable once the billing period has commenced and access has been granted. If you believe you have been
          charged in error, contact us within 7 days of the charge at{' '}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>.
        </p>

        <h2>7. Payment disputes and chargebacks</h2>
        <p>
          If you initiate a payment dispute or chargeback with your bank or card provider in respect of a subscription
          period during which you had active access to the Platform, we reserve the right to suspend your account pending
          resolution. We will cooperate fully with any investigation and provide evidence of access and the terms agreed
          at checkout.
        </p>

        <h2>8. Governing law</h2>
        <p>
          These terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive
          jurisdiction of the courts of England and Wales.
        </p>

        <p className="not-prose text-sm text-gray-500 dark:text-gray-400 pt-8 border-t border-gray-200 dark:border-navy-600">
          <Link to="/" className="text-gold-600 dark:text-gold-400 hover:underline">
            ← Back to home
          </Link>
          {' · '}
          <Link to="/refund-policy" className="text-gold-600 dark:text-gold-400 hover:underline">
            Refund policy
          </Link>
        </p>
      </article>
    </div>
  )
}
